import { customerApi } from '../api/andhraYatriApi';

const SESSION_KEY = 'andhraYatriCustomerSession';
const BOOKINGS_KEY = 'andhraYatriBookings';
const SAVED_PLACES_KEY = 'andhraYatriSavedPlaces';
const LOCAL_CUSTOMERS_KEY = 'andhraYatriLocalCustomers';
const SESSION_EVENT = 'andhra-yatri-auth-change';
const SAVED_PLACES_EVENT = 'andhra-yatri-saved-places-change';

function getCustomerStorageId(customerId) {
  if (customerId) return String(customerId);
  const session = getCustomerSession();
  return String(session?.customer?._id || session?.customer?.id || session?.customer?.mobile || session?.customer?.email || 'guest');
}

function getSavedPlacesKey(customerId) {
  return `${SAVED_PLACES_KEY}:${getCustomerStorageId(customerId)}`;
}

function savedPlaceKey(place) {
  if (typeof place === 'string') return place.trim().toLowerCase();
  return String(place?.slug || place?.title || place?.name || '').trim().toLowerCase();
}

export function getCustomerSession() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY)) || null;
  } catch {
    return null;
  }
}

export function saveCustomerSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  window.dispatchEvent(new Event(SESSION_EVENT));
}

export function clearCustomerSession() {
  localStorage.removeItem(SESSION_KEY);
  window.dispatchEvent(new Event(SESSION_EVENT));
}

export function getLocalCustomers() {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_CUSTOMERS_KEY)) || [];
  } catch {
    return [];
  }
}

export function saveLocalCustomer(customer) {
  const customers = getLocalCustomers();
  const normalizedMobile = String(customer.mobile || '').replace(/\D/g, '').slice(0, 10);
  const normalizedEmail = String(customer.email || '').trim().toLowerCase();
  const nextCustomer = {
    id: customer.id || customer._id || `local-customer-${Date.now()}`,
    name: customer.name,
    email: normalizedEmail,
    mobile: normalizedMobile,
    password: customer.password,
    createdAt: customer.createdAt || new Date().toISOString(),
  };
  const others = customers.filter((item) => (
    String(item.mobile || '') !== normalizedMobile &&
    String(item.email || '').toLowerCase() !== normalizedEmail
  ));
  const next = [nextCustomer, ...others].slice(0, 20);
  localStorage.setItem(LOCAL_CUSTOMERS_KEY, JSON.stringify(next));
  return nextCustomer;
}

export function findLocalCustomer({ email, mobile, password }) {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  const normalizedMobile = String(mobile || '').replace(/\D/g, '').slice(0, 10);
  return getLocalCustomers().find((customer) => (
    customer.password === password &&
    (
      (normalizedEmail && String(customer.email || '').toLowerCase() === normalizedEmail) ||
      (normalizedMobile && String(customer.mobile || '') === normalizedMobile)
    )
  )) || null;
}

export function findExistingLocalCustomer({ email, mobile }) {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  const normalizedMobile = String(mobile || '').replace(/\D/g, '').slice(0, 10);
  const customers = getLocalCustomers();

  if (normalizedMobile) {
    const foundMobile = customers.find((c) => String(c.mobile || '') === normalizedMobile);
    if (foundMobile) return { type: 'mobile', customer: foundMobile };
  }
  if (normalizedEmail) {
    const foundEmail = customers.find((c) => String(c.email || '').toLowerCase() === normalizedEmail);
    if (foundEmail) return { type: 'email', customer: foundEmail };
  }
  return null;
}

export async function checkCustomerExistsOnline({ email, mobile }) {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  const normalizedMobile = String(mobile || '').replace(/\D/g, '').slice(-10);

  try {
    const res = await customerApi.getAllCustomers();
    const list = Array.isArray(res)
      ? res
      : (res?.results || res?.data || res?.customers || res?.userList || []);

    if (Array.isArray(list) && list.length > 0) {
      if (normalizedMobile && normalizedMobile.length === 10) {
        const foundMobile = list.find((item) => {
          const itemMobile = String(item.mobile || item.phone || item.mobile_number || item.phoneNumber || '').replace(/\D/g, '').slice(-10);
          return itemMobile && itemMobile === normalizedMobile;
        });
        if (foundMobile) return { exists: true, type: 'mobile', customer: foundMobile };
      }

      if (normalizedEmail) {
        const foundEmail = list.find((item) => {
          const itemEmail = String(item.email || item.email_id || item.emailAddress || '').trim().toLowerCase();
          return itemEmail && itemEmail === normalizedEmail;
        });
        if (foundEmail) return { exists: true, type: 'email', customer: foundEmail };
      }
    }
  } catch (err) {
    // If getAllCustomers fails or requires admin auth, proceed gracefully
  }

  return { exists: false };
}


export function onCustomerSessionChange(callback) {
  const handler = () => callback(getCustomerSession());
  window.addEventListener(SESSION_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(SESSION_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

export function getBookings() {
  try {
    return JSON.parse(localStorage.getItem(BOOKINGS_KEY)) || [];
  } catch {
    return [];
  }
}

export function addBookingSearch(booking) {
  const bookings = [booking, ...getBookings()].slice(0, 20);
  localStorage.setItem(BOOKINGS_KEY, JSON.stringify(bookings));
  window.dispatchEvent(new Event('andhra-yatri-bookings-change'));
  return bookings;
}

export function addCustomerBooking(booking) {
  const createdAt = new Date().toISOString();
  const normalized = {
    id: booking.id || `booking-${Date.now()}`,
    status: 'Confirmed',
    createdAt,
    ...booking,
  };
  const bookings = [normalized, ...getBookings()].slice(0, 30);
  localStorage.setItem(BOOKINGS_KEY, JSON.stringify(bookings));
  window.dispatchEvent(new Event('andhra-yatri-bookings-change'));
  return normalized;
}

export function getSavedPlaces(customerId) {
  try {
    return JSON.parse(localStorage.getItem(getSavedPlacesKey(customerId))) || [];
  } catch {
    return [];
  }
}

export function savePlace(place, customerId) {
  const saved = getSavedPlaces(customerId);
  const targetKey = savedPlaceKey(place);
  if (saved.some((item) => savedPlaceKey(item) === targetKey)) return saved;
  const next = [{ ...place, savedAt: place.savedAt || new Date().toISOString() }, ...saved].slice(0, 12);
  localStorage.setItem(getSavedPlacesKey(customerId), JSON.stringify(next));
  window.dispatchEvent(new Event(SAVED_PLACES_EVENT));
  return next;
}

export function removeSavedPlace(place, customerId) {
  const targetKey = savedPlaceKey(place);
  const next = getSavedPlaces(customerId).filter((item) => savedPlaceKey(item) !== targetKey);
  localStorage.setItem(getSavedPlacesKey(customerId), JSON.stringify(next));
  window.dispatchEvent(new Event(SAVED_PLACES_EVENT));
  return next;
}

export { SAVED_PLACES_EVENT, SESSION_EVENT };